/**
 * Windows Desktop API Service
 * Handles all API calls to the Windows desktop backend
 */

import axiosInstance from "../../../utils/axios";

/**
 * List all Windows desktops for the current user
 */
export const listWindowsDesktops = async () => {
  const response = await axiosInstance.get("windows");
  return response.data;
};

/**
 * Create a new Windows desktop
 * @param {Object} params - Desktop creation parameters
 * @param {string} params.template - Template/AMI to use
 * @param {string} [params.instanceType] - EC2 instance type
 * @param {number} [params.volumeSize] - Volume size in GB
 * @param {string} [params.userGroup] - User group for billing
 */
export const createWindowsDesktop = async (params) => {
  const response = await axiosInstance.post("windows", params);
  return response.data;
};

/**
 * Get a specific Windows desktop by ID
 * @param {string} id - Desktop ID
 */
export const getWindowsDesktop = async (id) => {
  const response = await axiosInstance.get(`windows/${id}`);
  return response.data;
};

/**
 * Delete a Windows desktop
 * @param {string} id - Desktop ID
 */
export const deleteWindowsDesktop = async (id) => {
  await axiosInstance.delete(`windows/${id}`);
};

/**
 * Perform an action on a Windows desktop
 * @param {string} id - Desktop ID
 * @param {string} action - Action to perform (start, stop, terminate)
 */
export const windowsDesktopAction = async (id, action) => {
  const response = await axiosInstance.post(`windows/${id}/action`, { action });
  return response.data;
};

/**
 * Get credentials for a Windows desktop
 * @param {string} id - Desktop ID
 */
export const getWindowsCredentials = async (id) => {
  const response = await axiosInstance.get(`windows/${id}/credentials`);
  return response.data;
};

/**
 * Poll desktop status until it reaches a target state
 * @param {string} id - Desktop ID
 * @param {string[]} targetStates - Array of states to wait for
 * @param {number} [timeout=300000] - Timeout in ms (default 5 min)
 * @param {number} [interval=5000] - Polling interval in ms
 * @returns {Promise<Object>} - Desktop object when target state reached
 */
export const waitForDesktopState = async (id, targetStates, timeout = 300000, interval = 5000) => {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const desktop = await getWindowsDesktop(id);

    if (targetStates.includes(desktop.phase)) {
      return desktop;
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  throw new Error(`Timeout waiting for desktop to reach state: ${targetStates.join(", ")}`);
};

/**
 * Fetch the instance types available to the current org.
 * Returns an array of { type, family, vcpu, ram, gpu, available, unavailableReason }.
 */
export const getAvailableInstanceTypes = async () => {
  const response = await axiosInstance.get("capacity/instance-types");
  return response.data;
};

const windowsApi = {
  listWindowsDesktops,
  createWindowsDesktop,
  getWindowsDesktop,
  deleteWindowsDesktop,
  windowsDesktopAction,
  getWindowsCredentials,
  waitForDesktopState,
  getAvailableInstanceTypes
};

export default windowsApi;
